import { useQuery } from "@tanstack/react-query";
import { type paths as customerPaths } from "@njbsoft/types/customer";
import { customerApiClient } from "@/lib/auth";
import { customerKeys } from "@/lib/query-keys";
import { useCustomerStore } from "@/store";

// The customer portal context: the caller's profile plus every account
// membership they hold a live account_members row for. The membership list
// drives the switcher; the active selection is a SERVER scope — the list reads
// thread it as `account_id` and the API narrows through the per-account
// definer helper.
type CustomerContextResponse =
  customerPaths["/me"]["get"]["responses"]["200"]["content"]["application/json"];

export type CustomerMembership = CustomerContextResponse["memberships"][number];

/**
 * The `GET /me` read behind `customerKeys.context()`: the signed-in layout's
 * query and the sign-in's fresh read share it, so both fill the same entry. A
 * refusal throws an error carrying the response status.
 */
export async function fetchCustomerContext(): Promise<CustomerContextResponse> {
  const { data, error, response } = await customerApiClient.GET("/me");
  if (error)
    throw Object.assign(new Error("customer context fetch failed"), {
      status: response.status,
      details: error,
    });
  return data;
}

export function useMyCustomerContext(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: customerKeys.context(),
    queryFn: fetchCustomerContext,
    // Callers on public routes MUST pass enabled:false while signed out — an
    // anonymous read answers 401, which the shared client turns into a full
    // session teardown and a hard navigation away from the page.
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, err: unknown) => {
      const status = (err as { status?: number }).status;
      if (status === 403 || status === 401) return false;
      return failureCount < 2;
    },
  });
}

// The stable key for a membership — its account id.
export function membershipKey(m: CustomerMembership): string {
  return m.account_id;
}

// Resolves the ACTIVE membership from the persisted account selection, falling
// back to the first membership when the selection is stale (a membership that
// ended) or absent (first sign-in).
export function resolveActiveMembership(
  memberships: CustomerMembership[],
  activeAccountId: string | null,
): CustomerMembership | null {
  if (memberships.length === 0) return null;
  if (activeAccountId) {
    const a = memberships.find((m) => m.account_id === activeAccountId);
    if (a) return a;
  }
  return memberships[0] ?? null;
}

export function useActiveMembership(): CustomerMembership | null {
  const { data } = useMyCustomerContext();
  const activeAccountId = useCustomerStore((s) => s.activeAccountId);
  return resolveActiveMembership(data?.memberships ?? [], activeAccountId);
}

// The account id every list read narrows on. It comes from the RESOLVED
// membership, not the raw store primitive: the layout re-pins a stale/absent
// persisted selection in an effect, so a read keyed on the store value would
// fire once unnarrowed (briefly showing another account's premises) and again
// after the re-pin. Null only while the caller holds no membership at all — a
// state the layout renders as AccessDenied instead of a workspace.
export function useActiveAccountId(): string | null {
  return useActiveMembership()?.account_id ?? null;
}
